import { Component, computed, forwardRef, inject, input, OnInit, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { SensorBlockService, SensorDetailDto, SensorItemDto } from '@artemis-software/wr-api';
import { BehaviorSubject, debounceTime, firstValueFrom } from 'rxjs';
import { NbPopoverDirective, NbPosition } from '@nebular/theme';
import SensorUnitEnum = SensorDetailDto.SensorUnitEnum;
import SensorPurposeEnum = SensorDetailDto.PurposeEnum;

const DIMENSION_UNITS: Record<string, SensorUnitEnum[]> = {
  ENERGY: [SensorUnitEnum.KilowattHour, SensorUnitEnum.MegawattHour],
  VOLUME: [SensorUnitEnum.CubicMeter, SensorUnitEnum.CubicCentimeter, SensorUnitEnum.Liter],
};

@Component({
  standalone: false,
  selector: 'wr-sensor-installation-select',
  templateUrl: './sensor-installation-select.component.html',
  styleUrl: './sensor-installation-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SensorInstallationSelectComponent),
      multi: true,
    },
  ],
})
export class SensorInstallationSelectComponent extends AbstractFormControl<SensorItemDto | undefined> implements OnInit {
  readonly NbPosition = NbPosition;

  @ViewChild(NbPopoverDirective) popover!: NbPopoverDirective;

  readonly buildingId = input.required<string>();
  readonly dimension = input.required<string>();
  readonly purposes = input<SensorPurposeEnum[]>([]);

  readonly showPurpose = computed(() => this.purposes().length !== 1);

  private readonly sensorBlockService = inject(SensorBlockService);

  readonly searchText$ = new BehaviorSubject<string>('');
  readonly sensors$ = new BehaviorSubject<SensorDetailDto[]>([]);

  private allSensors: SensorDetailDto[] = [];
  private readonly reload$ = new BehaviorSubject<void>(undefined);

  ngOnInit(): void {
    this.reload$.subscribe(() => this.loadAllSensors());
    this.searchText$.pipe(debounceTime(150)).subscribe(() => this.applyFilter());
  }

  private async loadAllSensors(): Promise<void> {
    const blocks = await firstValueFrom(
      this.sensorBlockService.findAllDetailDtos({ buildingId: this.buildingId(), size: 1000 }),
    );
    const validUnits = DIMENSION_UNITS[this.dimension()] ?? [];
    const allowedPurposes = this.purposes();
    this.allSensors = blocks
      .flatMap(b => b.sensors)
      .filter(s =>
        s.isActive &&
        validUnits.includes(s.sensorUnit) &&
        (!allowedPurposes.length || allowedPurposes.includes(s.purpose)),
      );
    this.applyFilter();
  }

  private applyFilter(): void {
    const text = this.searchText$.value.toLowerCase();
    if (!text) {
      this.sensors$.next(this.allSensors);
      return;
    }
    this.sensors$.next(
      this.allSensors.filter(
        s =>
          s.name.toLowerCase().includes(text) ||
          s.sensorBlockName.toLowerCase().includes(text),
      ),
    );
  }

  selectSensor(sensor: SensorDetailDto): void {
    this.value = sensor as SensorItemDto;
    this.popover?.hide();
  }

  removeSensor(): void {
    this.value = undefined;
  }

  onSearchChange(text: string): void {
    this.searchText$.next(text);
  }
}
